import React from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  Field,
  FieldProps,
  useField,
} from 'formik';
import classNames from 'classnames';

import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import * as types from 'hellospa/page/prep-and-send/data/types';
import TextField from 'hellospa/foundation/control/text-field';
import ErrorMessage from 'hellospa/components/error-message';
import ContactInput from 'hellospa/components/contact-input';
import styles from './index.scss';
import messages from './intl';

type Props = {
  index: number,
  recipient: types.Signer,
};

const Signer = ({ index, recipient }: Props) => {
  const intl = useIntl();
  const showRole = useSelector(selectors.allowSignerRole);
  const isEmbedded = useSelector(selectors.isEmbedded);
  const isDeepIntegration = useSelector(selectors.isDeepIntegration);

  const parent = `recipients.${index}`;

  const [, nameMeta] = useField<String>(`${parent}.name`);
  const [, emailMeta] = useField<String>(`${parent}.email`);

  return (
    <div
      className={styles.fields}
      data-qa-ref={`${parent}.fields`}
    >
      <div className={styles.fieldRow}>
        {showRole && recipient.role && (
          <div>
            <h4 data-qa-ref={`${parent}.role`}>{recipient.role.name}</h4>
          </div>
        )}
        {!isDeepIntegration && !recipient.role && (
          <div>
            <h4
              id={`${parent}.signerHeader`}
              data-qa-ref={`${parent}.signer`}
            >
              {nameMeta.value
                ? nameMeta.value
                : intl.formatMessage(messages.signerType)}
            </h4>
          </div>
        )}
      </div>

      <div className={styles.fieldRow}>
        <div
          className={classNames({
            [styles.errorFieldContainer]: nameMeta.touched && nameMeta.error,
          })}
        >
          <label htmlFor={`${parent}.name`}>
            {intl.formatMessage(messages.fieldNameLabel)}
          </label>
          <Field
            name={`${parent}.name`}
          >
            {({ field }: FieldProps) => <TextField
              className={classNames(styles.recipient)}
              id={`${parent}.name`}
              autoFocus={index === 0}
              data-qa-ref={`${parent}.fields.name`}
              placeholder={intl.formatMessage(messages.fieldNamePlaceholder)}
              {...field} />}
          </Field>
          <div
            className={styles.errorContainer}
            role="name-error"
            data-qa-ref={`${parent}.errors.name`}
          >
            <ErrorMessage
              className={styles.error}
              name={`${parent}.name`} />
          </div>
        </div>

        <div
          className={classNames({
            [styles.errorFieldContainer]: emailMeta.touched && emailMeta.error,
          })}
        >
          <label htmlFor={`${parent}.email`}>
            {intl.formatMessage(messages.fieldEmailLabel)}
          </label>
          <Field name={`${parent}.email`}>
            {({ field, form }: FieldProps) => (
              <ContactInput
                id={`${parent}.email`}
                data-qa-ref={`${parent}.fields.email`}
                placeholder={intl.formatMessage(messages.fieldEmailPlaceholder)}
                className={styles.dropdownField}
                onSelectContact={(contact: types.Contact) => {
                  form.setFieldValue(`${parent}.name`, contact.name);
                  form.setFieldValue(field.name, contact.value);
                  form.validateField(`${parent}.name`);
                  form.validateField(field.name);
                }}
                contactTypes={['E']}
                searchEnabled={!isEmbedded || isDeepIntegration}
                {...field}
                onChange={(value: string) => form.setFieldValue(field.name, value)} />
            )}
          </Field>
          <div
            className={styles.errorContainer}
            role="email-error"
            data-qa-ref={`${parent}.errors.email`}
          >
            <ErrorMessage
              className={styles.error}
              name={`${parent}.email`} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signer;
